import { useCallback, useMemo, useState } from 'react';

import { DropdownOption } from '../ToolbarDropdown';

interface FieldOption {
  field: string;
  label: string;
}

export interface UseToolbarDropdownsOptions {
  visibleColumns: Record<string, boolean>;
  groupOptions: FieldOption[];
  onColumnVisibilityChange?: (columnId: string, visible: boolean) => void;
  onGroupChange?: (field: string | null) => void;
  onFilterClick?: (eventOrFieldId?: React.MouseEvent<HTMLElement> | string) => void;
}

export interface UseToolbarDropdownsReturn {
  columnsAnchor: HTMLElement | null;
  filterAnchor: HTMLElement | null;
  groupDropdownOptions: DropdownOption[];
  handleColumnsClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleColumnsClose: () => void;
  handleColumnToggle: (columnId: string) => void;
  handleFilterClick: (eventOrFieldId?: React.MouseEvent<HTMLElement> | string) => void;
  handleFilterClose: () => void;
  handleGroupSelect: (field: string | null) => void;
}

const toDropdownOptions = (options: FieldOption[]): DropdownOption[] =>
  options.map((opt) => ({ id: opt.field, label: opt.label }));

const useToolbarDropdowns = ({
  visibleColumns,
  groupOptions,
  onColumnVisibilityChange,
  onGroupChange,
  onFilterClick,
}: UseToolbarDropdownsOptions): UseToolbarDropdownsReturn => {
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);

  const handleColumnsClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => setColumnsAnchor(event.currentTarget),
    [],
  );

  const handleColumnsClose = useCallback(() => setColumnsAnchor(null), []);

  const handleColumnToggle = useCallback(
    (columnId: string) => {
      const isCurrentlyVisible = visibleColumns[columnId] !== false;
      onColumnVisibilityChange?.(columnId, !isCurrentlyVisible);
    },
    [visibleColumns, onColumnVisibilityChange],
  );

  const handleFilterClick = useCallback(
    (eventOrFieldId?: React.MouseEvent<HTMLElement> | string) => {
      if (onFilterClick) {
        onFilterClick(eventOrFieldId);
      } else if (typeof eventOrFieldId !== 'string') {
        setFilterAnchor(eventOrFieldId?.currentTarget ?? null);
      }
    },
    [onFilterClick],
  );

  const handleFilterClose = useCallback(() => setFilterAnchor(null), []);

  const handleGroupSelect = useCallback(
    (field: string | null) => onGroupChange?.(field),
    [onGroupChange],
  );

  const groupDropdownOptions = useMemo(() => toDropdownOptions(groupOptions), [groupOptions]);

  return {
    columnsAnchor,
    filterAnchor,
    groupDropdownOptions,
    handleColumnsClick,
    handleColumnsClose,
    handleColumnToggle,
    handleFilterClick,
    handleFilterClose,
    handleGroupSelect,
  };
};

export default useToolbarDropdowns;
